"use client";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Search,
  User,
  Heart,
  Phone,
  Menu,
  X,
  ChevronRight,
  Settings,
} from "lucide-react";
import { fetchCategories, fetchSubCategories } from "../services/api";
import { useWishlist } from "../context/WishlistContext";
import { useAuth } from "../context/AuthContext";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  const { wishlistItems } = useWishlist();
  const { isAuthenticated, isAdmin } = useAuth();

  // Optimized state management
  const [categories, setCategories] = useState([]);
  const [categorySubcategories, setCategorySubcategories] = useState({});
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [loadingSubcategories, setLoadingSubcategories] = useState(new Set());
  const [loadedSubcategories, setLoadedSubcategories] = useState(new Set());
  const [allSubcategoriesLoaded, setAllSubcategoriesLoaded] = useState(false);
  const [loadingAllSubcategories, setLoadingAllSubcategories] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pendingSearchQuery, setPendingSearchQuery] = useState('');

  // Memoized all subcategories for search
  const allSubCategories = useMemo(() => {
    return Object.values(categorySubcategories).flat();
  }, [categorySubcategories]);

  // Handle scroll effect with throttling
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 10);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch only categories initially (1 API call)
  useEffect(() => {
    const fetchCategoriesOnly = async () => {
      try {
        const categoriesData = await fetchCategories();
        if (categoriesData && categoriesData.length > 0) {
          setCategories(categoriesData);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategoriesOnly();
  }, []);

  // Lazy load subcategories only when needed (on hover)
  const loadSubcategoriesForCategory = useCallback(
    async (categoryId) => {
      // Don't load if already loaded or currently loading
      if (
        loadedSubcategories.has(categoryId) ||
        loadingSubcategories.has(categoryId)
      ) {
        return;
      }

      setLoadingSubcategories((prev) => new Set(prev).add(categoryId));

      try {
        const subCats = await fetchSubCategories(categoryId);
        const category = categories.find((cat) => cat._id === categoryId);

        const formattedSubCats = subCats.map((subCat) => ({
          ...subCat,
          categoryName: category?.name?.en || "",
          categoryId: categoryId,
        }));

        setCategorySubcategories((prev) => ({
          ...prev,
          [categoryId]: formattedSubCats,
        }));

        setLoadedSubcategories((prev) => new Set(prev).add(categoryId));
      } catch (error) {
        console.error(
          `Error fetching subcategories for category ${categoryId}:`,
          error
        );
        setCategorySubcategories((prev) => ({
          ...prev,
          [categoryId]: [],
        }));
      } finally {
        setLoadingSubcategories((prev) => {
          const newSet = new Set(prev);
          newSet.delete(categoryId);
          return newSet;
        });
      }
    },
    [categories, loadedSubcategories, loadingSubcategories]
  );

  // Load all subcategories for search functionality
  const loadAllSubcategoriesForSearch = useCallback(async (currentQuery = '') => {
    if (allSubcategoriesLoaded || loadingAllSubcategories || categories.length === 0) {
      // If already loaded and we have a query, mark it for search
      if (allSubcategoriesLoaded && currentQuery) {
        setPendingSearchQuery(currentQuery);
      }
      return;
    }

    setLoadingAllSubcategories(true);
    setSearchLoading(true);

    try {
      // Load subcategories for all categories that aren't already loaded
      const categoriesToLoad = categories.filter(
        (category) => !loadedSubcategories.has(category._id)
      );

      if (categoriesToLoad.length === 0) {
        setAllSubcategoriesLoaded(true);
        setLoadingAllSubcategories(false);
        setSearchLoading(false);
        if (currentQuery) {
          setPendingSearchQuery(currentQuery);
        }
        return;
      }

      // Load all subcategories in parallel
      const promises = categoriesToLoad.map(async (category) => {
        try {
          const subCats = await fetchSubCategories(category._id);
          return {
            categoryId: category._id,
            categoryName: category.name.en,
            subcategories: subCats.map((subCat) => ({
              ...subCat,
              categoryName: category.name.en,
              categoryId: category._id,
            })),
          };
        } catch (error) {
          console.error(
            `Error fetching subcategories for category ${category._id}:`,
            error
          );
          return {
            categoryId: category._id,
            categoryName: category.name.en,
            subcategories: [],
          };
        }
      });

      const results = await Promise.all(promises);

      // Update state with all loaded subcategories
      setCategorySubcategories((prev) => {
        const updated = { ...prev };
        results.forEach((result) => {
          updated[result.categoryId] = result.subcategories;
        });
        return updated;
      });

      // Mark all categories as loaded
      setLoadedSubcategories((prev) => {
        const newSet = new Set(prev);
        results.forEach((result) => {
          newSet.add(result.categoryId);
        });
        return newSet;
      });

      setAllSubcategoriesLoaded(true);
      
      // Mark query for search after data is loaded
      if (currentQuery) {
        setPendingSearchQuery(currentQuery);
      }
    } catch (error) {
      console.error("Error loading all subcategories for search:", error);
    } finally {
      setLoadingAllSubcategories(false);
      setSearchLoading(false);
    }
  }, [categories, loadedSubcategories, allSubcategoriesLoaded, loadingAllSubcategories]);

  // Handle category hover with debouncing
  const handleCategoryHover = useCallback(
    (categoryId) => {
      setHoveredCategory(categoryId);

      // Load subcategories if not already loaded
      if (categoryId && !loadedSubcategories.has(categoryId)) {
        // Small delay to avoid unnecessary API calls on quick hovers
        const timer = setTimeout(() => {
          loadSubcategoriesForCategory(categoryId);
        }, 150);

        return () => clearTimeout(timer);
      }
    },
    [loadSubcategoriesForCategory, loadedSubcategories]
  );

  // Optimized search with debouncing
  const performSearch = useCallback((query) => {
    if (query.length > 0) {
      const filteredResults = allSubCategories
        .filter((subCat) =>
          subCat?.name?.toLowerCase().includes(query.toLowerCase())
        )
        .slice(0, 5);

      setSearchResults(filteredResults);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [allSubCategories]);

  const debouncedSearch = useCallback(
    debounce((query) => {
      performSearch(query);
    }, 300),
    [performSearch]
  );

  // Handle search input with debouncing
  const handleSearchChange = useCallback(
    (e) => {
      const query = e.target.value;
      setSearchQuery(query);
      
      if (query.length > 0) {
        // Show search results dropdown immediately
        setShowSearchResults(true);
        
        // If subcategories are already loaded, search immediately
        if (allSubcategoriesLoaded) {
          debouncedSearch(query);
        } else {
          // Load all subcategories and search after loading
          setSearchLoading(true);
          loadAllSubcategoriesForSearch(query);
        }
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
        setSearchLoading(false);
      }
    },
    [debouncedSearch, allSubcategoriesLoaded, loadAllSubcategoriesForSearch]
  );

  // Handle search submission
  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (searchQuery.trim()) {
        navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        setSearchQuery("");
        setShowSearchResults(false);
        setShowMobileSearch(false);
      }
    },
    [searchQuery, navigate]
  );

  // Handle subcategory selection from search results
  const handleSubCategorySelect = useCallback(
    (subCategory) => {
      navigate(`/products/${subCategory.categoryId}/${subCategory._id}`, {
        state: {
          title: subCategory.name,
          categoryName: subCategory.categoryName,
        },
      });
      setSearchQuery("");
      setShowSearchResults(false);
      setShowMobileSearch(false);
    },
    [navigate]
  );

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [navigate]);

  // Trigger search when subcategories are updated and we have a search query
  useEffect(() => {
    if (searchQuery.length > 0 && allSubCategories.length > 0 && !searchLoading && !loadingAllSubcategories) {
      performSearch(searchQuery);
    }
  }, [allSubCategories, searchQuery, performSearch, searchLoading, loadingAllSubcategories]);

  // Handle pending search queries
  useEffect(() => {
    if (pendingSearchQuery && allSubCategories.length > 0 && !searchLoading && !loadingAllSubcategories) {
      performSearch(pendingSearchQuery);
      setPendingSearchQuery('');
    }
  }, [pendingSearchQuery, allSubCategories, performSearch, searchLoading, loadingAllSubcategories]);

  // Animation styles (moved to external CSS file would be better)
  const animationStyles = `
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    @keyframes slideIn {
      from { transform: translateY(-100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    
    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.05); }
      100% { transform: scale(1); }
    }
    
    .search-input {
      transition: all 0.3s ease;
    }
    
    .search-input:focus {
      box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.5);
    }
    
    .search-results {
      animation: fadeIn 0.3s ease-out forwards;
    }
    
    .search-result-item {
      transition: all 0.2s ease;
    }
    
    .search-result-item:hover {
      background-color: rgba(212, 175, 55, 0.1);
    }
    
    .nav-item {
      position: relative;
      transition: all 0.3s ease;
    }
    
    .nav-item::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 50%;
      width: 0;
      height: 2px;
      background: #D4AF37;
      transition: all 0.3s ease;
      transform: translateX(-50%);
    }
    
    .nav-item:hover::after {
      width: 70%;
    }
    
    .nav-item.active::after {
      width: 70%;
    }
    
    .icon-btn {
      transition: all 0.3s ease;
    }
    
    .icon-btn:hover {
      transform: translateY(-3px);
    }
    
    .mobile-menu {
      animation: fadeIn 0.3s ease-out forwards;
    }
    
    .mobile-search {
      animation: slideIn 0.3s ease-out forwards;
    }
    
    .search-icon-pulse {
      animation: pulse 2s infinite;
    }

    .dropdown {
      animation: fadeIn 0.3s ease-out forwards;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    }

    .dropdown-item {
      transition: all 0.2s ease;
    }

    .dropdown-item:hover {
      background-color: rgba(212, 175, 55, 0.1);
    }

    .loading-spinner {
      width: 16px;
      height: 16px;
      border: 2px solid #f3f3f3;
      border-top: 2px solid #D4AF37;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        isScrolled ? "shadow-md" : ""
      }`}
    >
      <style>{animationStyles}</style>

      {/* Top bar with contact info */}
      <div className="bg-gradient-to-r from-black to-gray-900 text-white py-1.5 hidden sm:block">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center text-xs">
            <div className="hidden md:block"></div>
            <div className="flex items-center">
              <a
                href="tel:+919892086190"
                className="flex items-center text-[#d4af37] hover:text-[#f5e7a3] transition-colors"
              >
                <Phone size={14} className="mr-1" />
                <span>+91 9892086190</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main header with logo, search and icons */}
      <div className="bg-black text-white">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center flex-shrink-0 mr-2">
              <div className="relative h-10 w-32 sm:h-14 sm:w-48 -mt-2">
                <img
                  src="/alka-logo.png"
                  alt="Alka Jewellers"
                  className="object-contain h-full w-full"
                  loading="eager"
                />
              </div>
            </Link>

            {/* Search Bar */}
            <div
              className="hidden sm:block relative flex-grow max-w-3xl mx-2 md:mx-4"
              ref={searchRef}
            >
              <form onSubmit={handleSearchSubmit} className="relative">
                <div className="relative overflow-hidden rounded-full shadow-sm focus-within:shadow-md transition-all duration-300">
                  <input
                    type="search"
                    placeholder="Search..."
                    className="w-full bg-white border border-gold-700 focus:border-[#d4af37] rounded-full py-2 pr-12 pl-4 text-black focus:outline-none search-input placeholder-gray-400 text-sm"
                    aria-label="Search"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  <button
                    type="submit"
                    className="absolute right-0 top-0 h-full px-3 flex items-center justify-center text-[#d4af37] hover:text-[#f5e7a3] transition-colors"
                    aria-label="Submit search"
                  >
                    <Search
                      size={18}
                      className={searchQuery ? "search-icon-pulse" : ""}
                    />
                  </button>
                </div>
              </form>

              {/* Enhanced Search Results Dropdown */}
              {showSearchResults && (
                <div className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-xl z-50 max-h-80 overflow-y-auto search-results border border-gray-200">
                  <div className="py-2">
                    {(searchLoading || loadingAllSubcategories) && searchResults.length === 0 && (
                      <div className="px-4 py-3 flex items-center justify-center">
                        <div className="loading-spinner"></div>
                        <span className="ml-2 text-sm text-gray-500">
                          Loading suggestions...
                        </span>
                      </div>
                    )}
                    {!searchLoading && !loadingAllSubcategories && searchResults.length === 0 && searchQuery.length > 0 && (
                      <div className="px-4 py-3 text-center text-gray-500">
                        <Search size={16} className="mx-auto mb-2 opacity-50" />
                        <div className="text-sm">No sub categories found for "{searchQuery}"</div>
                      </div>
                    )}
                    {searchResults.length > 0 && (
                      <>
                        {searchResults.map((result) => (
                          <div
                            key={result._id}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center search-result-item"
                            onClick={() => handleSubCategorySelect(result)}
                          >
                            <div className="w-12 h-12 mr-3 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 border border-gray-200">
                              {result.image ? (
                                <img
                                  src={result.image}
                                  alt={result.name}
                                  className="w-full h-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400">
                                  <Search size={16} />
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-gray-800 text-[#d4af37]">
                                {result.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                in{" "}
                                <span className="text-gray-700">
                                  {result.categoryName}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="px-4 py-3 border-t border-gray-100">
                          <button
                            onClick={handleSearchSubmit}
                            className="text-[#d4af37] text-sm hover:underline w-full text-left flex items-center"
                          >
                            <Search size={14} className="mr-2" />
                            See all results for "
                            <span className="font-medium">{searchQuery}</span>"
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right side icons */}
            <div className="flex items-center space-x-4">
              {/* Mobile search button */}
              <button
                className="sm:hidden text-[#d4af37] p-2 focus:outline-none icon-btn"
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                aria-label="Search"
              >
                <Search size={20} />
              </button>

              {/* Admin Panel Link */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="hidden md:flex text-white hover:text-[#d4af37] transition-colors flex-col items-center icon-btn"
                  aria-label="Admin Panel"
                >
                  <div className="relative p-1.5 rounded-full hover:bg-gray-800 transition-colors">
                    <Settings size={20} />
                  </div>
                  <span className="text-[10px] mt-1">Admin</span>
                </Link>
              )}

              {/* Profile */}
              <Link
                to="/profile"
                className="hidden md:flex text-white hover:text-[#d4af37] transition-colors flex-col items-center icon-btn"
                aria-label="Profile"
              >
                <div className="relative p-1.5 rounded-full hover:bg-gray-800 transition-colors">
                  <User size={20} />
                </div>
                <span className="text-[10px] mt-1">Profile</span>
              </Link>

              {/* Wishlist */}
              <Link
                to="/wishlist"
                className="text-white hover:text-[#d4af37] transition-colors flex flex-col items-center icon-btn"
                aria-label="Wishlist"
              >
                <div className="relative p-1.5 rounded-full hover:bg-gray-800 transition-colors">
                  <Heart size={20} />
                  {isAuthenticated && wishlistItems.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-[#d4af37] to-[#f5e7a3] text-black rounded-full w-4 h-4 flex items-center justify-center text-[10px] font-bold">
                      {wishlistItems.length}
                    </span>
                  )}
                </div>
                <span className="text-[10px] mt-1 hidden sm:inline-block">
                  Wishlist
                </span>
              </Link>

              {/* Mobile menu button */}
              <button
                className="md:hidden text-[#d4af37] p-1 focus:outline-none icon-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-expanded={mobileMenuOpen}
                aria-label="Toggle navigation menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </div>

     {/* Mobile Search Overlay */}
{showMobileSearch && (
  <div className="sm:hidden bg-black py-3 px-4 mobile-search relative z-60">
    <form onSubmit={handleSearchSubmit} className="relative">
      <div className="relative overflow-hidden rounded-full shadow-sm focus-within:shadow-md transition-all duration-300">
        <input
          type="search"
          placeholder="Search for jewelry..."
          className="w-full bg-gray-800 border border-gray-700 focus:border-[#d4af37] rounded-full py-2 pr-12 pl-4 text-white focus:outline-none search-input placeholder-gray-400 text-sm"
          aria-label="Search"
          value={searchQuery}
          onChange={handleSearchChange}
          autoFocus
        />
        <button
          type="submit"
          className="absolute right-0 top-0 h-full px-3 flex items-center justify-center text-[#d4af37] hover:text-[#f5e7a3] transition-colors"
          aria-label="Submit search"
        >
          <Search size={18} />
        </button>
      </div>
    </form>

    {/* Mobile Search Results */}
    {showSearchResults && (
      <div className="mt-2 bg-white rounded-xl shadow-xl max-h-60 overflow-y-auto search-results relative z-70">
        <div className="py-2">
          {(searchLoading || loadingAllSubcategories) && searchResults.length === 0 && (
            <div className="px-4 py-3 flex items-center justify-center">
              <div className="loading-spinner"></div>
              <span className="ml-2 text-sm text-gray-500">
                Loading suggestions...
              </span>
            </div>
          )}
          {!searchLoading && !loadingAllSubcategories && searchResults.length === 0 && searchQuery.length > 0 && (
            <div className="px-4 py-3 text-center text-gray-500">
              <Search size={14} className="mx-auto mb-2 opacity-50" />
              <div className="text-sm">No results found for "{searchQuery}"</div>
            </div>
          )}
          {searchResults.length > 0 && (
            <>
              {searchResults.map((result) => (
                <div
                  key={result._id}
                  className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center search-result-item active:bg-gray-100"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubCategorySelect(result);
                  }}
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSubCategorySelect(result);
                  }}
                >
                  <div className="w-10 h-10 mr-3 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {result.image ? (
                      <img
                        src={result.image}
                        alt={result.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400">
                        <Search size={14} />
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="font-medium text-sm text-[#d4af37]">
                      {result.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      in{" "}
                      <span className="text-gray-700">
                        {result.categoryName}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    )}
  </div>
)}

      {/* Main Navigation Bar */}
      <div className="bg-gradient-to-r from-gray-100 to-gray-50 shadow-sm">
        <div className="container mx-auto">
          <div className="hidden md:block">
            <div className="flex items-center justify-center py-2">
              {categories.map((category) => (
                <div
                  key={category._id}
                  className="relative group"
                  onMouseEnter={() => handleCategoryHover(category._id)}
                  onMouseLeave={() => setHoveredCategory(null)}
                >
                  <Link
                    to={`/category/${category._id}`}
                    className={`text-gray-800 hover:text-[#d4af37] transition-colors px-4 py-1.5 text-xs font-semibold tracking-wide nav-item ${
                      hoveredCategory === category._id ? "active" : ""
                    }`}
                  >
                    {category.name.en.toUpperCase()}
                  </Link>

                  {/* Dropdown for subcategories */}
                  {hoveredCategory === category._id && (
                    <div className="absolute left-0 mt-1 w-56 bg-white rounded-md shadow-lg dropdown z-50">
                      <div className="py-2">
                        {loadingSubcategories.has(category._id) ? (
                          <div className="px-4 py-2 flex items-center justify-center">
                            <div className="loading-spinner"></div>
                            <span className="ml-2 text-sm text-gray-500">
                              Loading...
                            </span>
                          </div>
                        ) : categorySubcategories[category._id]?.length > 0 ? (
                          <>
                            {categorySubcategories[category._id].map(
                              (subCategory) => (
                                <Link
                                  key={subCategory._id}
                                  to={`/products/${category._id}/${subCategory._id}`}
                                  state={{
                                    title: subCategory.name,
                                    categoryName: category.name.en,
                                  }}
                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#d4af37] dropdown-item"
                                >
                                  {subCategory.name}
                                </Link>
                              )
                            )}
                            <Link
                              to={`/category/${category._id}`}
                              className="block px-4 py-2 text-sm font-medium text-[#d4af37] hover:bg-gray-50 border-t border-gray-100 mt-1 dropdown-item"
                            >
                              View All {category.name.en}
                            </Link>
                          </>
                        ) : (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No subcategories available
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-gradient-to-b from-black to-gray-900 max-h-[80vh] overflow-y-auto mobile-menu fixed inset-x-0 top-[57px] z-50">
          <div className="container mx-auto px-4 py-4">
            {/* Mobile search */}
            <div className="mb-6" ref={searchRef}>
              <form onSubmit={handleSearchSubmit} className="relative">
                <div className="relative overflow-hidden rounded-full shadow-sm focus-within:shadow-md transition-all duration-300">
                  <input
                    type="search"
                    placeholder="Search for jewelry..."
                    className="w-full bg-gray-800 border border-gray-700 focus:border-[#d4af37] rounded-full py-2 pr-12 pl-4 text-white focus:outline-none search-input placeholder-gray-400 text-sm"
                    aria-label="Search"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                  <button
                    type="submit"
                    className="absolute right-0 top-0 h-full px-3 flex items-center justify-center text-[#d4af37] hover:text-[#f5e7a3] transition-colors"
                    aria-label="Submit search"
                  >
                    <Search size={18} />
                  </button>
                </div>
              </form>

             {/* Mobile Menu Search Results */}
{showSearchResults && (
  <div className="mt-2 bg-white rounded-xl shadow-xl max-h-60 overflow-y-auto search-results relative z-60">
    <div className="py-2">
      {(searchLoading || loadingAllSubcategories) && searchResults.length === 0 && (
        <div className="px-4 py-3 flex items-center justify-center">
          <div className="loading-spinner"></div>
          <span className="ml-2 text-sm text-gray-500">
            Loading suggestions...
          </span>
        </div>
      )}
      {!searchLoading && !loadingAllSubcategories && searchResults.length === 0 && searchQuery.length > 0 && (
        <div className="px-4 py-3 text-center text-gray-500">
          <Search size={14} className="mx-auto mb-2 opacity-50" />
          <div className="text-sm">No results found for "{searchQuery}"</div>
        </div>
      )}
      {searchResults.length > 0 && (
        <>
          {searchResults.map((result) => (
            <div
              key={result._id}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer flex items-center search-result-item active:bg-gray-100"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubCategorySelect(result);
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSubCategorySelect(result);
              }}
            >
              <div className="w-10 h-10 mr-3 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                {result.image ? (
                  <img
                    src={result.image}
                    alt={result.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400">
                    <Search size={14} />
                  </div>
                )}
              </div>
              <div>
                <div className="font-medium text-sm text-[#d4af37]">
                  {result.name}
                </div>
                <div className="text-xs text-gray-500">
                  in{" "}
                  <span className="text-gray-700">
                    {result.categoryName}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  </div>
)}
            </div>

            {/* Mobile menu navigation */}
            <nav className="grid gap-4">
              {categories.map((category) => (
                <div key={category._id}>
                  <Link
                    to={`/category/${category._id}`}
                    className="text-white hover:text-[#d4af37] transition-colors py-3 border-b border-gray-800 font-semibold text-sm flex justify-between items-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span>{category.name.en.toUpperCase()}</span>
                    <ChevronRight size={16} className="text-[#d4af37]" />
                  </Link>
                </div>
              ))}

              <Link
                to="/profile"
                className="text-white hover:text-[#d4af37] transition-colors py-3 border-b border-gray-800 font-semibold text-sm flex justify-between items-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span>MY PROFILE</span>
                <User size={16} className="text-[#d4af37]" />
              </Link>

              {isAdmin && (
                <Link
                  to="/admin"
                  className="text-white hover:text-[#d4af37] transition-colors py-3 border-b border-gray-800 font-semibold text-sm flex justify-between items-center"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>ADMIN PANEL</span>
                  <Settings size={16} className="text-[#d4af37]" />
                </Link>
              )}
            </nav>

            {/* Additional mobile menu items */}
            <div className="mt-6 pt-4 border-t border-gray-800">
              <h3 className="text-[#d4af37] text-xs font-semibold mb-3 uppercase tracking-wider">
                Customer Service
              </h3>
              <div className="grid gap-3">
                <Link
                  to="/about"
                  className="text-gray-300 hover:text-white text-sm py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About Us
                </Link>
                <Link
                  to="/contact"
                  className="text-gray-300 hover:text-white text-sm py-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Contact Us
                </Link>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-800">
                <a
                  href="tel:+919892086190"
                  className="flex items-center text-[#d4af37] hover:text-[#f5e7a3] transition-colors py-2"
                >
                  <Phone size={16} className="mr-2" />
                  <span>+91 9892086190</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default Header;
